import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GetClaimCompareExportRepository } from '../claim-charting-repository';
import type { ClaimFeatureItem, CompareFeatureItem, CompareFeatureItem1vN, CompareItem, ExcelParams, ExcelParams1vN, FeatureItem } from '../../types';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import type { ExportExcelService } from '../export-excel-service';

@injectable()
export class ExportExcelServiceImpl implements ExportExcelService {
    @inject(GetClaimCompareExportRepository)
    private _getClaimCompareExportRepository!: GetClaimCompareExportRepository;
    constructor(
        @inject(I18nService)
        private _i18n: I18nService,
    ) {}

    /**
     * 生成日期字符串 yyyymmdd
     * @returns 格式化的日期字符串，例如: 20260115
     */
    private _getDateString(): string {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}${month}${day}`;
    }

    private _exportFeatureHeadConfig() {
        return {
            name: {
                key: 'claims',
                i18nValue: this._i18n.t('aiClaim.Claims'),
            },
            value: {
                key: 'features',
                i18nValue: this._i18n.t('aiClaim.Features'),
            },
            trans: {
                key: 'translates',
                i18nValue: this._i18n.t('patent-view.Not translated'),
                transRow: true,
            },
        };
    }

    private _exportCompareHeadConfig(claimPN, descPN) {
        return {
            name: {
                key: 'claims',
                i18nValue: this._i18n.t('aiClaim.Claims'),
            },
            claimValue: {
                key: 'claimFeatures',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${claimPN}`,
            },
            claimTrans: {
                key: 'claimTrans',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${claimPN}${this._i18n.t('patent-view.Not translated')}`,
                transRow: true,
            },
            descValue: {
                key: 'descFeatures',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${descPN}`,
            },
            descTrans: {
                key: 'descTrans',
                i18nValue: `${this._i18n.t('aiClaim.Features')}${descPN}${this._i18n.t('patent-view.Not translated')}`,
                transRow: true,
            },
            isPublic: {
                key: 'isPublic',
                i18nValue: this._i18n.t('aiClaim.PresenceInDesc'),
            },
            reason: {
                key: 'reason',
                i18nValue: this._i18n.t('aiClaim.AIExplanation'),
            },
        };
    }

    private _getHead(config: any, hasTransLang: boolean) {
        const configKeys = Object.keys(config);
        // 如果有翻译就在最后添加一列翻译, name：'translates'表示翻译列
        if (hasTransLang) {
            return configKeys.map(key => config[key].i18nValue);
        } else{
            return configKeys.filter(key => !config[key].transRow).map(key => config[key].i18nValue);
        }
    }

    private _getBody(tableData: ClaimFeatureItem[], getTr: (item: string[]) => string[]) {
        return tableData.flatMap(item => {
            return item.features.map((feature: FeatureItem, index: number) => {
                const { value, trans = '' } = feature;
                const name = index === 0 ? item.name : '';
                return getTr([name, value, trans]);
            });
        });
    }

    private _getTr(item: string[], headLength: number) {
        return item.slice(0, headLength);
    }

    private _downloadFile(url: string) {
        Object.assign(document.createElement('a'), { href: url, download: '' }).click();
    }

    public async exportClaimExcel(tableData: ClaimFeatureItem[], tableInfo: ExcelParams) {
        const head = this._getHead(this._exportFeatureHeadConfig(), tableInfo.hasTransLang);
        const body = this._getBody(tableData, item => this._getTr(item, head.length));
        const params = {
            fileName: `${new Date().getTime()}/Claim features - ${this._getDateString()}`,
            charts: [{
                sheetName: 'sheet1',
                head,
                body,
            }],
            ai_flag: true,
        };
        const url = await this._getClaimCompareExportRepository.getClaimCompareExport(params);
        if (url) this._downloadFile(url);
    }

    private _removeBTags(value: string): string {
        return value.replace(/<\/?b>/gi, '');
    }

    private _getBodyCompare(data: CompareFeatureItem[], getTr: (item: string[]) => string[]) {
        return data.flatMap(item => {
            return item.compareFeatures.map((feature: CompareItem, index: number) => {
                const { claimVal, claimTrans = '', compareVal ='', compareTrans = '', isPublic = '', reason ='' } = feature;
                const filteredClaimVal = this._removeBTags(claimVal);
                const filteredClaimTrans = this._removeBTags(claimTrans);
                const filteredCompareVal = this._removeBTags(compareVal);
                const filteredReason = this._removeBTags(reason);
                const filteredCompareTrans = this._removeBTags(compareTrans);
                const isPublicText = (isPublic === 'Yes' || isPublic === 'true') ? this._i18n.t('aiClaim.Yes') : this._i18n.t('aiClaim.No');
                const name = index === 0 ? item.name : '';
                return getTr([name, filteredClaimVal, filteredClaimTrans, filteredCompareVal, filteredCompareTrans, isPublicText, filteredReason]);
            });
        });
    }

    private _getTrCompare(item: string[], headLength: number) {
        // 不带翻译是5列,带翻译是7列
        if (headLength === 5) {
            const [name, claimVal, , compareVal, , isPublic, reason] = item;
            return [name, claimVal, compareVal, isPublic, reason];
        }
        return item.slice(0, headLength);
    }

    public async exportCompareExcel(data: CompareFeatureItem[], tableInfo: ExcelParams) {
        const head = this._getHead(this._exportCompareHeadConfig(tableInfo.claimPN, tableInfo.descPN), tableInfo.hasTransLang);
        const body = this._getBodyCompare(data, item => this._getTrCompare(item, head.length));
        const params = {
            fileName: `${new Date().getTime()}/Claim chart - ${this._getDateString()}`,
            charts: [{
                sheetName: 'sheet1',
                head,
                body,
            }],
            ai_flag: true,
        };
        const url = await this._getClaimCompareExportRepository.getClaimCompareExport(params);
        if (url) this._downloadFile(url);
    }

    /**
     * 导出 1vN 对比结果
     *
     * Excel 结构:
     * - 表头: Claims | Features (Target PN) | Reference doc 1 (PN) | Reference doc 2 | ...
     * - 单元格格式（Reference doc 列）:
     *   无翻译: {PresenceInDesc}: Yes/No\n{AIExplanation}: ...\n{feature}: ...
     *   有翻译: {PresenceInDesc}: Yes/No\n{AIExplanation}: ...\n{feature}: ...\n({Not translated}: ...)
     *   注: 所有标题使用国际化，翻译行整体用括号包裹
     */
    public async exportCompareExcel1vN(data: CompareFeatureItem1vN[], tableInfo: ExcelParams1vN) {
        const head = [
            this._i18n.t('aiClaim.Claims'),
            `${this._i18n.t('aiClaim.Features')} (${tableInfo.targetPN})`,
            ...tableInfo.comparisonHeaders,
        ];

        const body = data.map(item => this._build1vNRow(item, tableInfo.hasTransLang));

        const fileName = `${new Date().getTime()}/Claim chart - ${this._getDateString()}`;

        const params = {
            fileName,
            charts: [{
                sheetName: 'sheet1',
                head,
                body,
            }],
            ai_flag: true,
        };

        const url = await this._getClaimCompareExportRepository.getClaimCompareExport(params);
        if (url) this._downloadFile(url);
    }

    /**
     * 构建 1vN 单行数据
     */
    private _build1vNRow(item: CompareFeatureItem1vN, hasTransLang: boolean): string[] {
        const row: string[] = [];

        row.push(item.name);

        const targetCell = hasTransLang && item.targetTrans
            ? `${item.targetValue}\n(${this._i18n.t('patent-view.Not translated')}: ${item.targetTrans})`
            : item.targetValue;
        row.push(targetCell);

        item.comparisonResults.forEach(result => {
            const cellLines = [
                `${this._i18n.t('aiClaim.PresenceInDesc')}: ${result.relevancy ? this._i18n.t('aiClaim.Yes') : this._i18n.t('aiClaim.No')}`,
                `${this._i18n.t('aiClaim.AIExplanation')}: ${this._removeBTags(result.reason)}`,
                `${this._i18n.t('aiClaim.Features')}: ${this._removeBTags(result.comparisonValue)}`,
            ];

            // 仅当有翻译且内容不为空时添加，整行用括号包裹
            if (hasTransLang && result.comparisonTrans) {
                cellLines.push(`(${this._i18n.t('patent-view.Not translated')}: ${this._removeBTags(result.comparisonTrans)})`);
            }

            row.push(cellLines.join('\n'));
        });

        return row;
    }
}
